import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Typography, Tooltip, Link } from '@material-ui/core';

// CONSTANTS
import { ROUTE_SHOWS_AND_MOVIES_MOVIE, ROUTE_SHOWS_AND_MOVIES_SHOW } from '@/constants/routes';
import { ContentType } from '@/constants/watchList';

// UTILITY
import getContentYear, { getPlaceholderUrl } from '@/utility/content';
import getLink from '@/utility/routes';

// COMPONENTS
import RouterLink from '@/components/Link';

// STYLES
import { Container } from '../styles';

// HELP
import ActionsMenu from './ActionsMenu';

const SearchCardShowMovie = ({ className, data, link, analyticsLocation, actions, ...props }) => {
  const [t] = useTranslation();

  const image = React.useMemo(
    () =>
      data.thumbnail ||
      getPlaceholderUrl({ title: data.title, width: 71, height: 106, bgColor: 'F5F6F7' }),
    [data.thumbnail],
  );

  const to = React.useMemo(
    () =>
      link ||
      getLink(
        data.type === ContentType.MOVIE
          ? ROUTE_SHOWS_AND_MOVIES_MOVIE
          : ROUTE_SHOWS_AND_MOVIES_SHOW,
        {
          movie_id: data.slug || data.id,
          show_id: data.slug || data.id,
        },
      ),
    [link, data.slug, data.id],
  );

  const year = React.useMemo(() => getContentYear(data.first_air_date), [data.first_air_date]);

  const appNames = data.apps.slice(0, 3).map(app => app.name);
  const subAppNames =
    data.apps.length >= 3 ? data.apps.slice(3, data.apps.length).map(app => app.name) : [];

  return (
    <Container {...props} className={clsx('search-card', `search-card-show-movie`, className)}>
      <ActionsMenu className="search-card__button" actions={actions} />
      <RouterLink className="search-card__link" to={to} location={analyticsLocation} />

      <div className="search-card__column-group">
        <div className="search-card__column">
          <div className="search-card__image search-card-show-movie__image">
            <img src={image} alt="app-logo" />
          </div>
        </div>

        <div className="search-card__column">
          <Typography variant="h5">
            {data.title}
            <Typography className="p4" variant="inherit" component="span" color="textSecondary">
              {data.first_air_date && ` (${year})`}
            </Typography>
          </Typography>
        </div>
      </div>
      <div className="search-card__column-group">
        <div className="search-card__column">
          {!!appNames.length && (
            <>
              <Typography
                className="p4 search-card__availability-title"
                variant="inherit"
                component="p"
                color="textSecondary"
              >
                {t('common.availableOn')}:
              </Typography>

              <Typography className="search-card__availabilities" variant="body2" component="p">
                {appNames.join(', ')}
                {!!subAppNames.length && (
                  <>
                    {', '}
                    <Tooltip title={subAppNames.join(', ')} interactive>
                      <Link className="search-card__link-more" variant="inherit">
                        {t('common.plusCountMore', { count: subAppNames.length })}{' '}
                      </Link>
                    </Tooltip>
                  </>
                )}
              </Typography>
            </>
          )}
        </div>
      </div>
    </Container>
  );
};

SearchCardShowMovie.defaultProps = {
  className: '',
  link: '',
};

SearchCardShowMovie.propTypes = {
  className: PropTypes.string,
  data: PropTypes.object.isRequired,
  link: PropTypes.string,
  analyticsLocation: PropTypes.string,
  actions: PropTypes.array.isRequired,
};

export default React.memo(SearchCardShowMovie);
